import { type Component, For, type JSX, type ParentComponent, Show, createContext, useContext } from "solid-js";
import { Dynamic } from "solid-js/web";
import type {
  ASTNode,
  CodeBlockASTNode,
  CustomBlockASTNode,
  CustomInlineASTNode,
  DocASTNode,
  HeadingASTNode,
  ItemASTNode,
  LinkASTNode,
  ListASTNode,
  TableASTNode,
  TableCellASTNode,
  TableWrapperASTNode,
} from "../toastmark/ast";
import { CodeBlockWithCopy } from "./CodeBlockWithCopy";

export type MarkdownRendererComponents = {
  document: Component<{ node: DocASTNode }>;
  heading: Component<{ node: HeadingASTNode }>;
  paragraph: Component<{ node: ASTNode }>;
  text: Component<{ node: ASTNode }>;
  strong: Component<{ node: ASTNode }>;
  strike: Component<{ node: ASTNode }>;
  emph: Component<{ node: ASTNode }>;
  code: Component<{ node: ASTNode }>;
  link: Component<{ node: LinkASTNode }>;
  image: Component<{ node: LinkASTNode }>;
  list: Component<{ node: ListASTNode }>;
  item: Component<{ node: ItemASTNode }>;
  codeBlock: Component<{ node: CodeBlockASTNode }>;
  blockQuote: Component<{ node: ASTNode }>;
  thematicBreak: Component<{ node: ASTNode }>;
  htmlBlock: Component<{ node: ASTNode }>;
  customBlock: Component<{ node: CustomBlockASTNode }>;
  table: Component<{ node: TableASTNode }>;
  tableWrapper: Component<{ node: TableWrapperASTNode }>;
  tableHead: Component<{ node: ASTNode }>;
  tableBody: Component<{ node: ASTNode }>;
  tableRow: Component<{ node: ASTNode }>;
  tableCell: Component<{ node: TableCellASTNode }>;
  customInline: Component<{ node: CustomInlineASTNode }>;
  unknown: Component<{ node: ASTNode }>;
  softbreak: Component<{ node: ASTNode }>;
  tableDelimRow: Component<{ node: ASTNode }>;
  tableDelimCell: Component<{ node: ASTNode }>;
  htmlInline: Component<{ node: ASTNode }>;
};
export type MarkdownRendererClasses = {
  [K in keyof MarkdownRendererComponents]:
    | string
    | ((node: Parameters<MarkdownRendererComponents[K]>[0]["node"]) => string);
};

export type MarkdownRendererOptions = Partial<{
  /**
   * Whether to auto-generate default classes for each markdown node, e.g.: `<p class="md-paragraph" />`
   * @default true
   */
  defaultClasses?: boolean;
  /**
   * The prefix to use for default classes if they're enabled, e.g.: `md-`
   * @default "md-"
   */
  classPrefix?: string;

  /**
   * Custom components to use for rendering markdown nodes.
   *
   * @example
   * ```ts
   * components: {
   *   heading: (props: { node: HeadingASTNode }) => <h1>{props.node.literal}</h1>,
   * }
   * ```
   */
  components: Partial<MarkdownRendererComponents>;

  /**
   * Custom classes to use for styling markdown nodes.
   * Use them when overriding components is too much
   *
   * @example
   * ```ts
   * classes: {
   *   heading: "text-2xl font-bold",
   * }
   * ```
   */
  classes: Partial<MarkdownRendererClasses>;
}>;

const getNodeClass = (node: ASTNode) => {
  const { options } = useSolidStreamingMarkdownContext();
  const classPrefix = options.classPrefix || "md-";
  const defaultClasses = options.defaultClasses ?? true;
  let result = "";
  if (defaultClasses) {
    result = `${classPrefix}${node.type}`;
  }
  const classFn = options.classes?.[node.type as keyof MarkdownRendererClasses];
  if (!classFn) {
    return result;
  }

  if (typeof classFn === "string") {
    result += ` ${classFn}`;
  } else {
    //@ts-ignore
    result += ` ${classFn(node)}`;
  }

  return result;
};

const defaultComponents: MarkdownRendererComponents = {
  document: (props) => (
    <div class={getNodeClass(props.node)}>
      <SolidStreamingMarkdownChildren node={props.node} />
    </div>
  ),
  heading: (props) => (
    <Dynamic component={`h${props.node.level}`} class={getNodeClass(props.node)}>
      <SolidStreamingMarkdownChildren node={props.node} />
    </Dynamic>
  ),
  paragraph: (props) => (
    <p class={getNodeClass(props.node)}>
      <SolidStreamingMarkdownChildren node={props.node} />
    </p>
  ),
  text: (props) => <span class={getNodeClass(props.node)}>{props.node.literal}</span>,
  strong: (props) => (
    <strong class={getNodeClass(props.node)}>
      <SolidStreamingMarkdownChildren node={props.node} />
    </strong>
  ),
  strike: (props) => (
    <span class={getNodeClass(props.node)} style={{ "text-decoration": "line-through" }}>
      <SolidStreamingMarkdownChildren node={props.node} />
    </span>
  ),
  emph: (props) => (
    <em class={getNodeClass(props.node)}>
      <SolidStreamingMarkdownChildren node={props.node} />
    </em>
  ),
  code: (props) => <code class={getNodeClass(props.node)}>{props.node.literal}</code>,
  link: (props) => (
    <a href={props.node.destination || "#"} class={getNodeClass(props.node)}>
      <SolidStreamingMarkdownChildren node={props.node} />
    </a>
  ),
  image: (props) => (
    <img src={props.node.destination || ""} alt={props.node.literal || ""} class={getNodeClass(props.node)} />
  ),
  list: (props) => (
    <Dynamic component={props.node.listData?.type === "ordered" ? "ol" : "ul"} class={getNodeClass(props.node)}>
      <SolidStreamingMarkdownChildren node={props.node} />
    </Dynamic>
  ),
  item: (props) => (
    <li class={getNodeClass(props.node)}>
      {props.node.listData.task && (
        <input type="checkbox" checked={props.node.listData.checked} disabled class="markdown-task-checkbox" />
      )}
      <SolidStreamingMarkdownChildren node={props.node} />
    </li>
  ),
  codeBlock: (props) => <CodeBlockWithCopy node={props.node} class={getNodeClass(props.node)} showCopyButton={true} />,
  blockQuote: (props) => (
    <blockquote class={getNodeClass(props.node)}>
      <SolidStreamingMarkdownChildren node={props.node} />
    </blockquote>
  ),
  thematicBreak: (props) => <hr class={getNodeClass(props.node)} />,
  htmlBlock: (props) => <div class={getNodeClass(props.node)} innerHTML={props.node.literal || ""} />,
  customBlock: (props) => <div class={getNodeClass(props.node)}>{props.node.literal}</div>,
  tableWrapper: (props) => (
    <div class={getNodeClass(props.node)}>
      <SolidStreamingMarkdownChildren node={props.node} />
    </div>
  ),
  table: (props) => (
    <table class={getNodeClass(props.node)}>
      <SolidStreamingMarkdownChildren node={props.node} />
    </table>
  ),
  tableHead: (props) => (
    <thead class={getNodeClass(props.node)}>
      <SolidStreamingMarkdownChildren node={props.node} />
    </thead>
  ),
  tableBody: (props) => (
    <tbody class={getNodeClass(props.node)}>
      <SolidStreamingMarkdownChildren node={props.node} />
    </tbody>
  ),
  tableRow: (props) => (
    <tr class={getNodeClass(props.node)}>
      <SolidStreamingMarkdownChildren node={props.node} />
    </tr>
  ),
  tableCell: (props) => {
    // For now, we'll just render the cell without alignment
    // In a real implementation, we would need to access the parent table node
    // to get column alignment information
    return (
      <td class={getNodeClass(props.node)}>
        <SolidStreamingMarkdownChildren node={props.node} />
      </td>
    );
  },
  htmlInline: () => null,
  customInline: (props) => <span class={getNodeClass(props.node)}>{props.node.literal}</span>,
  unknown: (props) => (
    <Show
      when={props.node.children.length > 0}
      fallback={<span class={getNodeClass(props.node)}>{props.node.literal}</span>}
    >
      <span class={getNodeClass(props.node)}>
        <SolidStreamingMarkdownChildren node={props.node} />
      </span>
    </Show>
  ),
  softbreak: () => <br />,
  tableDelimRow: () => null,
  tableDelimCell: () => null,
};

const MarkdownRendererContext = createContext({
  options: {
    components: {
      ...defaultComponents,
    },
  } as MarkdownRendererOptions & {
    components: MarkdownRendererComponents;
  },
});

export const useSolidStreamingMarkdownContext = () => useContext(MarkdownRendererContext);
export const SolidStreamingMarkdownContextProvider: ParentComponent<{
  options: MarkdownRendererOptions;
}> = (props) => {
  return (
    <MarkdownRendererContext.Provider
      value={{
        options: {
          ...props.options,
          components: {
            ...defaultComponents,
            ...(props.options?.components || {}),
          },
        },
      }}
    >
      {props.children}
    </MarkdownRendererContext.Provider>
  );
};

export const SolidStreamingMarkdownChildren = (props: {
  node: ASTNode;
}): JSX.Element => {
  return <For each={props.node.children}>{(child) => <ASTNodeRenderer node={child} />}</For>;
};

export const ASTNodeRenderer = (props: { node: ASTNode }): JSX.Element => {
  const { options } = useSolidStreamingMarkdownContext();
  const comp = options.components;

  return (
    <Dynamic
      component={
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        comp[props.node.type as keyof MarkdownRendererComponents] || comp.unknown
      }
      node={props.node}
    />
  );
};
